import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Settings, X, Check } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true,
    analytics: true,
    advertising: true
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Small delay so the page loads first
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
    setVisible(false);
    setShowCustomize(false);

    // If analytics disabled, try to remove GA cookies
    if (!consent.analytics) {
      document.cookie.split(';').forEach(c => {
        const name = c.trim().split('=')[0];
        if (name.startsWith('_ga') || name.startsWith('_gid')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        }
      });
    }
  };

  const acceptAll = () => saveConsent({ essential: true, analytics: true, advertising: true });
  const rejectOptional = () => saveConsent({ essential: true, analytics: false, advertising: false });
  const saveCustom = () => saveConsent(prefs);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none" data-testid="cookie-consent-banner">
      <div className="pointer-events-auto w-full max-w-2xl animate-in slide-in-from-bottom-4">
        {/* Main Banner */}
        {!showCustomize ? (
          <div className="bg-gray-950 border border-gray-800 rounded-2xl shadow-2xl shadow-black/50 p-6">
            <div className="flex items-start gap-4">
              <div className="bg-[#F4B400]/10 p-2.5 rounded-xl flex-shrink-0">
                <Shield className="text-[#F4B400]" size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base mb-1.5">We value your privacy</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We use cookies to enhance your experience, analyze traffic, and serve personalized ads through Google AdSense. You can accept all, customize, or view our{' '}
                  <Link to="/cookies" className="text-[#F4B400] hover:underline" onClick={() => setVisible(false)}>Cookie Policy</Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 mt-5">
              <button
                onClick={acceptAll}
                className="flex-1 bg-[#F4B400] text-black font-semibold py-2.5 px-5 rounded-xl hover:bg-[#d49f00] transition-colors text-sm"
                data-testid="cookie-accept-all"
              >
                Accept All
              </button>
              <button
                onClick={rejectOptional}
                className="flex-1 bg-white/10 text-white font-medium py-2.5 px-5 rounded-xl hover:bg-white/20 transition-colors text-sm"
                data-testid="cookie-reject-optional"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="flex-1 border border-gray-700 text-gray-300 font-medium py-2.5 px-5 rounded-xl hover:border-[#F4B400] hover:text-[#F4B400] transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="cookie-customize"
              >
                <Settings size={14} /> Customize
              </button>
            </div>
          </div>
        ) : (
          /* Customize Panel */
          <div className="bg-gray-950 border border-gray-800 rounded-2xl shadow-2xl shadow-black/50 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold text-base">Cookie Preferences</h3>
              <button onClick={() => setShowCustomize(false)} className="text-gray-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 mb-5">
              {/* Essential */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                <div>
                  <p className="text-white text-sm font-medium">Essential Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Required for the website to function</p>
                </div>
                <div className="bg-green-500/20 text-green-400 text-xs font-medium px-3 py-1 rounded-full">Always On</div>
              </div>

              {/* Analytics */}
              <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 cursor-pointer group">
                <div>
                  <p className="text-white text-sm font-medium">Analytics Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Google Analytics - traffic and usage data</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-700 rounded-full peer-checked:bg-[#F4B400] transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform" />
                </div>
              </label>

              {/* Advertising */}
              <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 cursor-pointer group">
                <div>
                  <p className="text-white text-sm font-medium">Advertising Cookies</p>
                  <p className="text-gray-500 text-xs mt-0.5">Google AdSense - personalized ads</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={prefs.advertising}
                    onChange={(e) => setPrefs(p => ({ ...p, advertising: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-700 rounded-full peer-checked:bg-[#F4B400] transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform" />
                </div>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={saveCustom}
                className="flex-1 bg-[#F4B400] text-black font-semibold py-2.5 px-5 rounded-xl hover:bg-[#d49f00] transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="cookie-save-prefs"
              >
                <Check size={14} /> Save Preferences
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 bg-white/10 text-white font-medium py-2.5 px-5 rounded-xl hover:bg-white/20 transition-colors text-sm"
              >
                Accept All
              </button>
            </div>

            <p className="text-gray-600 text-xs text-center mt-4">
              Learn more in our{' '}
              <Link to="/cookies" className="text-[#F4B400] hover:underline" onClick={() => setVisible(false)}>Cookie Policy</Link>
              {' '}&middot;{' '}
              <Link to="/privacy" className="text-[#F4B400] hover:underline" onClick={() => setVisible(false)}>Privacy Policy</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
